/**
 * __FLEET_TRACKING__ (2026-10-03) one driver's day: summary, chronological activity
 * (stops, gaps, geofence events, stop visits, speeding, sign-on/off), jobs vs GPS, exports.
 * Presentational - FleetTrackingView computes everything and passes it in.
 */
import React, { useMemo, useState } from 'react';
import { fmtDur, fmtClock, tsMs } from '../utils/fleetTrack';

const S = {
  h: { color: 'var(--text-secondary)', fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0.9rem 0 0.4rem' },
  stat: { background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '0.4rem 0.5rem' },
  statL: { color: 'var(--text-muted)', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  statV: { color: 'var(--text-primary)', fontSize: '0.95rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  btn: { padding: '0.3rem 0.6rem', background: 'var(--btn-secondary-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontSize: '0.75rem' },
  row: { display: 'flex', gap: '0.5rem', padding: '0.35rem 0.25rem', borderBottom: '1px solid var(--border-muted)', cursor: 'pointer', fontSize: '0.8rem', alignItems: 'flex-start' },
};

const EVENT_TEXT = {
  enter: 'Entered stop fence', exit: 'Left stop fence', dwell: 'Dwelling at stop', completion_pending: 'Completion pending',
  drive_by: 'Drove past stop', gps_gap: 'GPS gap (reported)', late_arrival: 'Late arrival', break_movement: 'Moved during break',
  break_movement_ended: 'Break movement ended',
};

const KIND_COLOR = { stop: '#d29922', gap: '#f85149', event: '#a371f7', visit: '#58a6ff', speed: '#f85149', shift: '#8b949e', first: '#3fb950', last: '#3fb950' };

const delta = (m) => {
  if (m == null) return null;
  const c = m > 20 ? '#f85149' : m > 5 ? '#d29922' : m < -5 ? '#58a6ff' : '#3fb950';
  const t = m > 0 ? `${m}m late` : m < 0 ? `${-m}m early` : 'on time';
  return <span style={{ color: c, fontWeight: 600 }}>{t}</span>;
};

function FleetHistoryPanel({
  driverName, truckLabel, track, stops, gaps, summary, speeding, events, shifts, loads, compare,
  speedLimit, onJump, onExport, onClose,
}) {
  const [filter, setFilter] = useState('all');

  const items = useMemo(() => {
    const out = [];
    if (track && track.length) {
      out.push({ kind: 'first', ms: track[0].t, title: 'First ping of the day', lat: track[0].lat, lng: track[0].lng });
      out.push({ kind: 'last', ms: track[track.length - 1].t, title: 'Last ping', lat: track[track.length - 1].lat, lng: track[track.length - 1].lng });
    }
    (stops || []).forEach((s, i) => out.push({ kind: 'stop', ms: s.arriveMs, endMs: s.departMs, title: `Stop ${i + 1} · ${fmtDur(s.durMin)}${s.ongoing ? ' (still there)' : ''}`, sub: s.place || '', lat: s.lat, lng: s.lng }));
    (gaps || []).forEach((g) => out.push({ kind: 'gap', ms: g.startMs, endMs: g.endMs, title: `No GPS for ${fmtDur(g.durMin)}`, sub: g.distM > 300 ? `moved ${(g.distM / 1000).toFixed(1)} km meanwhile` : 'did not move', lat: g.from.lat, lng: g.from.lng }));
    (speeding || []).forEach((e) => out.push({ kind: 'speed', ms: e.startMs, endMs: e.endMs, title: `Speeding ${e.maxKmh} km/h (limit ${speedLimit})`, sub: `${e.durSec}s`, lat: e.lat, lng: e.lng }));
    (events || []).forEach((e) => {
      const ms = tsMs(e.created_at);
      if (ms == null) return;
      out.push({ kind: 'event', ms, title: EVENT_TEXT[e.event_type] || e.event_type, sub: [e.stop_type, e.address].filter(Boolean).join(' · '), lat: e.latitude, lng: e.longitude });
    });
    (loads || []).forEach((l) => (l.load_stops || []).forEach((s) => {
      const a = tsMs(s.actual_arrival_time) != null ? tsMs(s.actual_arrival_time) : tsMs(s.actual_arrival);
      const d = tsMs(s.actual_departure_time) != null ? tsMs(s.actual_departure_time) : tsMs(s.actual_departure);
      const where = s.place_name || (s.address || '').split(',')[0];
      const lat = s.latitude != null ? Number(s.latitude) : null, lng = s.longitude != null ? Number(s.longitude) : null;
      if (a != null) out.push({ kind: 'visit', ms: a, endMs: d, title: `Arrived ${s.stop_type || 'stop'} · #${l.load_number || ''}`, sub: where, lat, lng });
      if (d != null) out.push({ kind: 'visit', ms: d, title: `Departed ${s.stop_type || 'stop'} · #${l.load_number || ''}`, sub: where, lat, lng });
    }));
    (shifts || []).forEach((s) => {
      const a = tsMs(s.signed_in_at), b = tsMs(s.signed_out_at);
      if (a != null) out.push({ kind: 'shift', ms: a, title: 'Signed on' });
      if (b != null) out.push({ kind: 'shift', ms: b, title: 'Signed off' });
    });
    return out.sort((x, y) => x.ms - y.ms);
  }, [track, stops, gaps, speeding, events, loads, shifts, speedLimit]);

  const shown = filter === 'all' ? items : items.filter((i) => i.kind === filter || (filter === 'visit' && i.kind === 'event'));
  const sm = summary || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1rem' }}>{driverName}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{truckLabel}</div>
        </div>
        <button style={S.btn} onClick={onClose} title="Back to the fleet list">✕</button>
      </div>

      {!track || !track.length ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '1rem 0' }}>No GPS recorded for this driver on this day.</div>
      ) : (
        <>
          <div style={S.h}>Day summary</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.35rem' }}>
            <div style={S.stat}><div style={S.statL}>Distance</div><div style={S.statV}>{sm.distanceKm} km</div></div>
            <div style={S.stat}><div style={S.statL}>Driving</div><div style={S.statV}>{fmtDur(sm.driveMin)}</div></div>
            <div style={S.stat}><div style={S.statL}>Stopped</div><div style={S.statV}>{fmtDur(sm.dwellMin)}</div></div>
            <div style={S.stat}><div style={S.statL}>Stops</div><div style={S.statV}>{sm.stops}</div></div>
            <div style={S.stat}><div style={S.statL}>First ping</div><div style={S.statV}>{fmtClock(sm.firstMs)}</div></div>
            <div style={S.stat}><div style={S.statL}>Last ping</div><div style={S.statV}>{fmtClock(sm.lastMs)}</div></div>
            <div style={S.stat}><div style={S.statL}>Max speed</div><div style={S.statV}>{sm.maxSpeedKmh} km/h</div></div>
            <div style={S.stat}><div style={S.statL}>Avg moving</div><div style={S.statV}>{sm.avgMovingKmh} km/h</div></div>
            <div style={S.stat} title={sm.gapDistKm ? `${sm.gapDistKm} km covered while not reporting (straight line)` : ''}><div style={S.statL}>GPS gaps</div><div style={S.statV}>{fmtDur(sm.gapMin)}</div></div>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
            <button style={S.btn} onClick={() => onExport('csv')}>⬇ CSV</button>
            <button style={S.btn} onClick={() => onExport('gpx')}>⬇ GPX</button>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', alignSelf: 'center' }}>{sm.pings} pings · {(speeding || []).length} speeding</span>
          </div>
        </>
      )}

      {(compare || []).length > 0 && (
        <>
          <div style={S.h}>Jobs vs GPS</div>
          {(loads || []).map((l) => {
            const rows = compare.filter((c) => c.load_id === l.id);
            if (!rows.length) return null;
            return (
              <div key={l.id} style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', marginBottom: '0.4rem', overflow: 'hidden' }}>
                <div style={{ background: 'var(--surface-2)', padding: '0.3rem 0.5rem', fontSize: '0.78rem', display: 'flex', justifyContent: 'space-between' }}>
                  <strong>#{l.load_number || '—'}</strong><span style={{ color: 'var(--text-secondary)' }}>{(l.status || '').replace(/_/g, ' ')}</span>
                </div>
                {rows.map((c) => (
                  <div key={c.id} style={{ ...S.row, flexDirection: 'column', gap: '0.15rem' }}
                    onClick={() => onJump(c.gpsArrMs || c.recArrMs || c.plannedMs, null)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span><span style={{ color: c.stop_type === 'pickup' ? '#58a6ff' : '#3fb950', fontWeight: 700, marginRight: 4 }}>{c.stop_type === 'pickup' ? 'P' : c.stop_type === 'delivery' ? 'D' : '•'}</span>{(c.label || '').split(',')[0] || 'Stop'}</span>
                      {delta(c.lateVsPlanMin)}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.72rem', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', width: '100%' }}>
                      <span>Plan {fmtClock(c.plannedMs)}</span>
                      <span title="geofence / driver tap">Recorded {fmtClock(c.recArrMs)}</span>
                      <span title="first dwell within 300 m">GPS {c.visited ? fmtClock(c.gpsArrMs) : (c.nearestM != null ? `not within 300 m (closest ${c.nearestM >= 1000 ? (c.nearestM / 1000).toFixed(1) + ' km' : c.nearestM + ' m'})` : '—')}</span>
                    </div>
                    {c.gpsDwellMin != null && <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>On site {fmtDur(c.gpsDwellMin)} by GPS{c.recVsGpsMin != null && Math.abs(c.recVsGpsMin) > 5 ? ` · recorded ${Math.abs(c.recVsGpsMin)}m ${c.recVsGpsMin > 0 ? 'after' : 'before'} GPS arrival` : ''}</div>}
                  </div>
                ))}
              </div>
            );
          })}
        </>
      )}

      {items.length > 0 && (
        <>
          <div style={{ ...S.h, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Activity</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ background: 'var(--surface-input)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 4, fontSize: '0.7rem' }}>
              <option value="all">All</option><option value="stop">Stops</option><option value="gap">GPS gaps</option>
              <option value="visit">Stop visits + geofence</option><option value="speed">Speeding</option><option value="shift">Shift</option>
            </select>
          </div>
          <div>
            {shown.map((it, i) => (
              <div key={i} style={S.row} onClick={() => onJump(it.ms, it.lat != null && it.lng != null ? { lat: Number(it.lat), lng: Number(it.lng) } : null)} title="Jump the time bar here">
                <span style={{ width: 8, height: 8, borderRadius: 4, background: KIND_COLOR[it.kind], marginTop: 5, flexShrink: 0 }} />
                <span style={{ color: 'var(--text-secondary)', width: '4.6rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{fmtClock(it.ms)}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div>{it.title}{it.endMs ? <span style={{ color: 'var(--text-muted)' }}> → {fmtClock(it.endMs)}</span> : null}</div>
                  {it.sub ? <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.sub}</div> : null}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default FleetHistoryPanel;
